import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Formik } from 'formik';
import isEmail from 'validator/lib/isEmail';
import { useSelector } from 'react-redux';
import mixpanel from 'mixpanel-browser';
import { Form as UIForm } from 'semantic-ui-react';

// ACTIONS
import { clearErrors } from 'services/Users/actions';

// ASSETS
import PlusIcon from 'assets/PlusIcon';
import EditIcon from 'assets/EditIcon';

// COMPONENTS
import { useModal } from 'components/Modals';
import { AddButton, CancelButton, PrimaryButton, SaveButton, SecondaryButton, SVGButton } from 'components/Buttons';
import Form from 'componentsV2/Form';
import { FormInput, FormSelect } from 'componentsV2/Fields';
import ErrorMessages from 'components/ErrorMessages';

// CONSTANTS
import { USER_ROLES_TRANSLATION, PRO_PLAN_ROLES, PRO_PLUS_PLAN_ROLES } from 'constants/common';
import { ANALYTICS_EVENTS } from 'constants/mixpanel.constants';

import { useAbility } from 'permissions';

// SELECTORS
import { selectErrors, selectLoading } from 'services/Users/selectors';
import { selectUser } from 'services/Authentication/selectors';
// import { selectChargeById } from 'services/FleetSettings/selectors';

// STYLES
import { Header, Footer, Title, Content } from 'styles/modals';
import { GlobalErrors } from 'styles/form';
import colors from 'styles/colors';

// THUNKS
import { addUser, changeUser, removeUser } from 'services/Users/thunks';

import { useAppDispatch } from 'hooks/store';
import { BUTTON_INTENT } from 'styles/buttons';
import { useHierarchy } from 'hooks';
import TreeSelect from 'components/TreeSelect';
import { mapDealershipHierarchyForDropdowns } from 'utils/dealershipHierarchy.utils';
import { HierarchyType } from 'services/resources';
import { SelectedEntityType } from 'services/Hierarchy/actions';

const Container = styled.div`
  border-radius: 4px;
  border: 1px solid ${colors.selectorBorder};
  background-color: ${colors.white};
`;

const StyledInput = styled(FormInput)`
  width: 400px;
`;

const StyledForm = styled(Form)`
  margin-top: 14px;
`;

const RoleSelector = (props) => (
  <FormSelect
    // @ts-expect-error TS(2769): No overload matches this call.
    name="userRole"
    label={{
      translationId: 'views.FleetSettings.UserManagement.Accounts.addUserModal.roleLabel',
    }}
    portalTargetRef={props.modalRef}
    options={USER_ROLES_TRANSLATION.filter(
      (item) =>
        (!Object.values(PRO_PLAN_ROLES).includes(item.id) && !Object.values(PRO_PLUS_PLAN_ROLES).includes(item.id)) ||
        (Object.values(PRO_PLAN_ROLES).includes(item.id) && props.ability.can('view', 'planner')) ||
        (Object.values(PRO_PLUS_PLAN_ROLES).includes(item.id) && props.ability.can('view', 'valet'))
    ).map((role) => ({
      key: role.id,
      label: { translationId: role.translationId },
      value: role.id,
    }))}
  />
);

const AddUser = (props) => {
  const { hierarchy, currentlySelectedEntity, hasChildren } = useHierarchy();
  const user = useSelector(selectUser());
  const mappedHierarchy = mapDealershipHierarchyForDropdowns(hierarchy, user);
  let initialDealershipId: SelectedEntityType | undefined;
  if (currentlySelectedEntity !== HierarchyType.HIERARCHY) {
    initialDealershipId = currentlySelectedEntity;
  }

  if (currentlySelectedEntity === HierarchyType.SELF) {
    initialDealershipId = user.profile.dealership.id;
  }

  const intl = useIntl();
  const dispatch = useAppDispatch();
  const clientErrors = useSelector(selectErrors());
  const loading = useSelector(selectLoading());

  const ability = useAbility();

  const { openModal, closeModal, isOpen, Modal, modalRef } = useModal({
    background: `${colors.primary}88`,
    closeOnOutsideClick: true,
  });

  const formValidation = (values) => {
    const errors = {};

    if (!isEmail(values.email)) {
      // @ts-expect-error TS(2339): Property 'email' does not exist on type '{}'.
      errors.email = intl.formatMessage({
        id: 'validators.email.emailIsNotValid',
      });
    }

    if (hasChildren && !values.dealershipId) {
      // @ts-expect-error TS(2339): Property 'dealershipId' does not exist on type '{}'.
      errors.dealershipId = intl.formatMessage({
        id: 'validators.required.fieldIsRequired',
      });
    }

    return errors;
  };

  useEffect(() => {
    if (loading === false && !Object.keys(clientErrors).length && isOpen) {
      closeModal();
    }
  }, [loading]);

  const handleModalCancel = () => {
    closeModal();
    dispatch(clearErrors());
  };

  const handleButtonClick = (e) => {
    mixpanel.track(ANALYTICS_EVENTS.CREATE_ACCOUNT);
    openModal(e);
    if (props.onClick) {
      props.onClick(e);
    }
  };

  return (
    <>
      <AddButton id="user-management-add-user" onClick={handleButtonClick} icon={<PlusIcon color={colors.fleetBlue} />}>
        {intl.formatMessage({
          id: 'views.FleetSettings.UserManagement.Accounts.addUserModal.triggerButton',
        })}
      </AddButton>

      {isOpen && (
        <Modal>
          <Container>
            <Header>
              <Title>
                {intl.formatMessage({
                  id: 'views.FleetSettings.UserManagement.Accounts.addUserModal.title',
                })}
              </Title>
            </Header>
            <Formik
              onSubmit={(values) => {
                dispatch(addUser(values.email, values.userRole, values.dealershipId));
              }}
              validate={formValidation}
              initialValues={{
                email: '',
                userRole: 0,
                dealershipId: initialDealershipId,
              }}
            >
              {({ values, errors, touched, isValid, submitForm, setFieldValue }) => (
                <>
                  <Content>
                    {intl.formatMessage({
                      id: 'views.FleetSettings.UserManagement.Accounts.addUserModal.description',
                    })}
                    <StyledForm id="new-user-form">
                      {hasChildren && (
                        <UIForm.Field>
                          <label htmlFor="hierarchy-tree-select">
                            {intl.formatMessage({
                              id: 'views.FleetSettings.UserManagement.Accounts.addUserModal.selectADealership',
                            })}
                          </label>
                          <TreeSelect
                            id="hierarchy-tree-select"
                            initialValue={initialDealershipId}
                            options={mappedHierarchy}
                            onValueChange={(value) => setFieldValue('dealershipId', value)}
                          />
                        </UIForm.Field>
                      )}
                      <StyledInput
                        // @ts-expect-error TS(2769): No overload matches this call.
                        name="email"
                        label={intl.formatMessage({
                          id: 'views.AccountSettings.Creation.form.emailLabel',
                        })}
                        type="text"
                        error={Boolean(touched.email && errors.email)}
                      />
                      <RoleSelector modalRef={modalRef} ability={ability} />
                      <GlobalErrors>
                        {Boolean(Object.keys(clientErrors).length) && <ErrorMessages errors={clientErrors} />}
                      </GlobalErrors>
                    </StyledForm>
                  </Content>
                  <Footer justifyContent="space-between">
                    <CancelButton onClick={handleModalCancel} />
                    <PrimaryButton
                      id="user-create-account"
                      onClick={submitForm}
                      disabled={!values.email || (hasChildren && !values.dealershipId) || !isValid}
                      loading={loading}
                    >
                      {intl.formatMessage({
                        id: 'views.FleetSettings.UserManagement.Accounts.addUserModal.createAccountButton',
                      })}
                    </PrimaryButton>
                  </Footer>
                </>
              )}
            </Formik>
          </Container>
        </Modal>
      )}
    </>
  );
};

const EditUser = (props) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const loading = useSelector(selectLoading());
  const clientErrors = useSelector(selectErrors());
  const currentUser = useSelector(selectUser());

  // const charge = useSelector(selectChargeById(props.chargeId));

  const ability = useAbility();

  const { openModal, closeModal, isOpen, Modal, modalRef } = useModal({
    background: `${colors.primary}88`,
  });

  const handleModalCancel = () => {
    closeModal();
    dispatch(clearErrors());
  };

  const handleButtonClick = (e) => {
    mixpanel.track(ANALYTICS_EVENTS.EDIT_USER_ACCOUNT);
    openModal(e);
    if (props.onClick) {
      props.onClick(e);
    }
  };

  // immediately after creation, the profile.dealership may still be an object. Handle edge case
  const userDealership =
    typeof props.user.profile.dealership === 'number'
      ? props.user.profile.dealership
      : props.user.profile.dealership.id;

  const handleDelete = () => {
    mixpanel.track(ANALYTICS_EVENTS.DELETE_ACCOUNT);
    dispatch(removeUser(props.user.id, userDealership));
  };

  useEffect(() => {
    if (loading === false && !Object.keys(clientErrors).length && isOpen) {
      closeModal();
    }
  }, [loading]);

  const isSelf = currentUser.id === props.user.id;

  return (
    <>
      <SVGButton className="user-management-edit-user" onClick={handleButtonClick}>
        <EditIcon color={colors.fleetBlue} />
      </SVGButton>

      {isOpen && (
        <Modal>
          <Container>
            {/* @ts-expect-error TS(2769): No overload matches this call. */}
            <Header justifyContent={isSelf ? 'flex-start' : 'space-between'} flexDirection="row" alignItems="center">
              <Title>
                {intl.formatMessage({
                  id: 'views.FleetSettings.UserManagement.Accounts.editUserModal.title',
                })}
              </Title>
              {!isSelf && (
                <SecondaryButton onClick={handleDelete} intent={BUTTON_INTENT.danger}>
                  {intl.formatMessage({
                    id: 'views.FleetSettings.UserManagement.Accounts.editUserModal.deleteAccountButton',
                  })}
                </SecondaryButton>
              )}
            </Header>
            <Formik
              onSubmit={(values) => {
                dispatch(changeUser(props.user.profile.id, values.userRole, userDealership));
              }}
              initialValues={{
                email: props.user.email,
                userRole: USER_ROLES_TRANSLATION.find((role) => role.id === props.user.profile.role)?.id || 0,
              }}
            >
              {({ values, touched, initialValues, submitForm }) => (
                <>
                  <Content>
                    <StyledForm>
                      <StyledInput
                        // @ts-expect-error TS(2769): No overload matches this call.
                        name="email"
                        label={intl.formatMessage({
                          id: 'views.AccountSettings.Creation.form.emailLabel',
                        })}
                        type="text"
                        disabled
                      />
                      <RoleSelector modalRef={modalRef} ability={ability} />

                      <GlobalErrors>
                        {Boolean(Object.keys(clientErrors).length) && <ErrorMessages errors={clientErrors} />}
                      </GlobalErrors>
                    </StyledForm>
                  </Content>
                  {/* @ts-expect-error TS(2769): No overload matches this call. */}
                  <Footer justifyContent="flex-end">
                    <CancelButton onClick={handleModalCancel} />
                    <SaveButton
                      type="submit"
                      onClick={submitForm}
                      disabled={!(touched.userRole && initialValues.userRole !== values.userRole)}
                      loading={loading}
                    />
                  </Footer>
                </>
              )}
            </Formik>
          </Container>
        </Modal>
      )}
    </>
  );
};

export default { AddUser, EditUser };
